/**
 * Game View Component
 * Main student game interface that orchestrates all game components
 */

import React, { useState, useEffect, useRef } from 'react';
import { GameState } from '../models/GameState';
import { PlayerState } from '../models/Player';
import { QuestionId, AnswerValue } from '../models/Question';
import { questionHandler, stageManager } from '../engine';
import { narrativeManager, contentLoader } from '../content';

import StageView from './StageView';
import NarrativeDisplay from './NarrativeDisplay';
import QuestionPanel from './QuestionPanel';
import InventoryDisplay from './InventoryDisplay';
import CharacterMessagePanel from './CharacterMessagePanel';
import TransmissionBriefing from './TransmissionBriefing';

interface GameViewProps {
  gameState: GameState;
  player: PlayerState;
  onSubmitAnswer: (questionId: QuestionId, answer: AnswerValue) => void;
  answeredQuestions?: Set<string>;
  onMessagesAcknowledged?: () => void;
  className?: string;
}

export const GameView: React.FC<GameViewProps> = ({
  gameState,
  player,
  onSubmitAnswer,
  answeredQuestions = new Set(),
  onMessagesAcknowledged,
  className = ''
}) => {
  const [showNarrative, setShowNarrative] = useState(true);
  const [showTransmissionBriefing, setShowTransmissionBriefing] = useState(false);
  const [currentNarrativeIndex, setCurrentNarrativeIndex] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [reviewMode, setReviewMode] = useState<'narrative' | 'briefing' | null>(null);
  const inventoryScrollRef = useRef<HTMLDivElement>(null);
  const [showInventoryScrollIndicator, setShowInventoryScrollIndicator] = useState(false);

  // Get current questions for this player
  const currentQuestions = stageManager.getCurrentQuestions(gameState);
  const availableQuestions = currentQuestions.filter(qId =>
    questionHandler.isQuestionAvailableForPlayer(qId, player.playerId, gameState)
  );

  // Get stage narratives (with video URLs attached based on game state)
  const stageNarratives = narrativeManager.getStageEntryNarratives(
    gameState.currentStage,
    gameState.currentVariant || undefined,
    gameState
  );

  const currentNarrative = stageNarratives[currentNarrativeIndex];

  // Load character messages for briefing
  const stageCharacterMessages = contentLoader.getStageCharacterMessages(
    gameState.currentStage,
    player.role
  );

  // Get stage progress
  const stageProgress = stageManager.getStageProgress(gameState);

  // Handle narrative advancement
  const handleNextNarrative = () => {
    if (currentNarrativeIndex < stageNarratives.length - 1) {
      setCurrentNarrativeIndex(currentNarrativeIndex + 1);
    } else {
      // Narratives done — show briefing if messages exist, otherwise go to questions
      if (stageCharacterMessages.length > 0) {
        setShowNarrative(false);
        setShowTransmissionBriefing(true);
      } else {
        setShowNarrative(false);
      }
    }
  };

  // Handle briefing completion
  const handleBriefingComplete = () => {
    setShowTransmissionBriefing(false);
    onMessagesAcknowledged?.();
  };

  // Reset narrative display when stage changes
  useEffect(() => {
    setShowNarrative(true);
    setShowTransmissionBriefing(false);
    setCurrentNarrativeIndex(0);
    setVideoPlaying(false);
    setReviewMode(null);
  }, [gameState.currentStage]);

  // Handle edge case: no narratives but has messages — show briefing directly
  useEffect(() => {
    if (stageNarratives.length === 0 && stageCharacterMessages.length > 0) {
      setShowNarrative(false);
      setShowTransmissionBriefing(true);
    }
  }, [gameState.currentStage, stageNarratives.length, stageCharacterMessages.length]);

  // Set videoPlaying when current narrative has a video
  useEffect(() => {
    if (currentNarrative?.videoUrl) {
      setVideoPlaying(true);
    } else {
      setVideoPlaying(false);
    }
  }, [currentNarrativeIndex, currentNarrative?.videoUrl]);

  // Check if inventory is scrollable and update indicator visibility
  useEffect(() => {
    const checkInventoryScrollable = () => {
      const container = inventoryScrollRef.current;
      if (!container) return;

      const isScrollable = container.scrollHeight > container.clientHeight;
      const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 5;

      setShowInventoryScrollIndicator(isScrollable && !isAtBottom);
    };

    checkInventoryScrollable();
    const container = inventoryScrollRef.current;

    if (container) {
      container.addEventListener('scroll', checkInventoryScrollable);
      window.addEventListener('resize', checkInventoryScrollable);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', checkInventoryScrollable);
      }
      window.removeEventListener('resize', checkInventoryScrollable);
    };
  }, [gameState.inventory]);

  return (
    <div className={className} style={{ maxHeight: 'calc(100vh - 120px)' }}>
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 h-full">
        {/* Left Sidebar - 30% */}
        <div className="lg:col-span-3 flex flex-col gap-4 overflow-y-auto max-h-full lg:max-h-full max-h-[50vh] pr-2 scrollbar-thin">
          {/* Game Info (Stage + Team Status) */}
          <StageView
            stage={gameState.currentStage}
            huntingPermitDay={gameState.huntingPermitDay}
            progress={stageProgress}
            variant={gameState.currentVariant}
            teamName={gameState.teamName}
            teamEnergyPacks={gameState.teamEnergyPacks}
            alivePlayers={gameState.players.filter(p => p.isAlive).length}
            totalPlayers={gameState.players.length}
          />

          {/* Character Messages */}
          <CharacterMessagePanel
            stage={gameState.currentStage}
            role={player.role}
            timing="immediate"
          />

          {/* Inventory - Grows to fill remaining space */}
          {gameState.inventory.length > 0 && (
            <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm flex-1 min-h-0 flex flex-col relative">
              <div className="px-4 py-3 border-b border-[#CED4DE] flex-shrink-0">
                <h3 className="text-base font-bold text-[#12356B]">Inventory</h3>
              </div>
              <div ref={inventoryScrollRef} className="px-4 py-3 flex-1 min-h-0 overflow-y-auto scrollbar-thin">
                <InventoryDisplay
                  inventory={gameState.inventory}
                  compact
                  fillHeight
                />
              </div>

              {/* Scroll Indicator */}
              {showInventoryScrollIndicator && (
                <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none bg-gradient-to-t from-white via-white/80 to-transparent rounded-b-xl flex items-end justify-center pb-2">
                  <div className="flex items-center gap-1 text-xs text-slate-500 font-medium">
                    <span>Scroll for more</span>
                    <svg className="w-3 h-3 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Main Content Area - 70% */}
        <div className="lg:col-span-7 space-y-6 overflow-y-auto max-h-full pr-2 scrollbar-thin">
          {/* Narrative Display (initial) */}
          {showNarrative && !reviewMode && stageNarratives.length > 0 && (
            <div>
              <NarrativeDisplay
                narrative={currentNarrative}
                onVideoEnded={() => setVideoPlaying(false)}
              />
              <div className="mt-4 bg-white border border-[#CED4DE] rounded-xl shadow-sm px-6 py-4 flex items-center justify-end">
                <div className="flex items-center gap-4">
                  <span className="text-lg text-slate-600">
                    {currentNarrativeIndex + 1} / {stageNarratives.length}
                  </span>
                  <button
                    onClick={handleNextNarrative}
                    disabled={videoPlaying}
                    className={
                      videoPlaying
                        ? 'rounded-lg px-5 py-2.5 bg-[#CED4DE] text-slate-500 font-semibold opacity-60 cursor-not-allowed'
                        : 'rounded-lg px-5 py-2.5 bg-[#367A25] text-white font-semibold hover:bg-[#2d6820] transition-colors'
                    }
                  >
                    {videoPlaying
                      ? 'Video Playing...'
                      : currentNarrativeIndex < stageNarratives.length - 1
                        ? 'Continue'
                        : 'Begin'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Transmission Briefing (initial) */}
          {showTransmissionBriefing && !reviewMode && stageCharacterMessages.length > 0 && (
            <TransmissionBriefing
              messages={stageCharacterMessages}
              stage={gameState.currentStage}
              onComplete={handleBriefingComplete}
            />
          )}

          {/* Review Mode: Re-show narratives */}
          {reviewMode === 'narrative' && stageNarratives.length > 0 && (
            <div>
              <NarrativeDisplay
                narrative={stageNarratives[currentNarrativeIndex]}
                onVideoEnded={() => setVideoPlaying(false)}
              />
              <div className="mt-4 bg-white border border-[#CED4DE] rounded-xl shadow-sm px-6 py-4 flex items-center justify-between">
                <button
                  onClick={() => {
                    setReviewMode(null);
                    setVideoPlaying(false);
                  }}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-[#12356B] border border-[#CED4DE] hover:bg-[#E2E5EC] transition-colors"
                >
                  Back to Questions
                </button>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setCurrentNarrativeIndex(Math.max(0, currentNarrativeIndex - 1))}
                    disabled={currentNarrativeIndex === 0}
                    className="rounded-lg px-4 py-2 text-sm font-medium text-[#12356B] border border-[#CED4DE] hover:bg-[#E2E5EC] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-lg text-slate-600">
                    {currentNarrativeIndex + 1} / {stageNarratives.length}
                  </span>
                  {currentNarrativeIndex < stageNarratives.length - 1 ? (
                    <button
                      onClick={() => setCurrentNarrativeIndex(currentNarrativeIndex + 1)}
                      className="rounded-lg px-5 py-2.5 bg-[#367A25] text-white font-semibold hover:bg-[#2d6820] transition-colors"
                    >
                      Next
                    </button>
                  ) : stageCharacterMessages.length > 0 ? (
                    <button
                      onClick={() => setReviewMode('briefing')}
                      className="rounded-lg px-5 py-2.5 bg-[#367A25] text-white font-semibold hover:bg-[#2d6820] transition-colors"
                    >
                      View Briefing
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setReviewMode(null);
                        setVideoPlaying(false);
                      }}
                      className="rounded-lg px-5 py-2.5 bg-[#367A25] text-white font-semibold hover:bg-[#2d6820] transition-colors"
                    >
                      Back to Questions
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Review Mode: Re-show briefing */}
          {reviewMode === 'briefing' && stageCharacterMessages.length > 0 && (
            <TransmissionBriefing
              messages={stageCharacterMessages}
              stage={gameState.currentStage}
              onComplete={() => setReviewMode(null)}
            />
          )}

          {/* Review Buttons */}
          {!showNarrative && !showTransmissionBriefing && !reviewMode && (stageNarratives.length > 0 || stageCharacterMessages.length > 0) && (
            <div className="flex items-center gap-3">
              {stageNarratives.length > 0 && (
                <button
                  onClick={() => { setReviewMode('narrative'); setCurrentNarrativeIndex(0); setVideoPlaying(false); }}
                  className="flex items-center gap-1.5 text-sm font-medium text-[#12356B] hover:text-[#12356B]/80 transition-colors"
                >
                  <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  Review Story
                </button>
              )}
              {stageCharacterMessages.length > 0 && (
                <button
                  onClick={() => setReviewMode('briefing')}
                  className="flex items-center gap-1.5 text-sm font-medium text-[#12356B] hover:text-[#12356B]/80 transition-colors"
                >
                  <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                  </svg>
                  Review Briefing
                </button>
              )}
            </div>
          )}

          {/* Questions (show all - unanswered are editable, answered are read-only) */}
          {!showNarrative && !showTransmissionBriefing && !reviewMode && availableQuestions.length > 0 && (
            <div className="space-y-6">
              {availableQuestions.map((questionId) => {
                const question = questionHandler.getQuestion(questionId);
                const currentAnswer = player.decisions.get(questionId);
                const isAnswered = answeredQuestions.has(questionId);

                return (
                  <QuestionPanel
                    key={questionId}
                    question={question}
                    inventory={gameState.inventory}
                    maxValue={gameState.teamEnergyPacks}
                    onSubmit={(answer) => onSubmitAnswer(questionId, answer)}
                    disabled={isAnswered}
                    currentAnswer={currentAnswer}
                  />
                );
              })}
            </div>
          )}

          {/* Waiting State with Team Progress */}
          {!showNarrative && !showTransmissionBriefing && !reviewMode && availableQuestions.length === 0 && (
            <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm px-6 py-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-[#12356B] mb-2">All Questions Answered!</h3>
                <p className="text-slate-600">
                  Waiting for other team members to finish their responses...
                </p>
              </div>

              {/* Team Progress */}
              <div className="border border-[#CED4DE] rounded-lg overflow-hidden">
                <div className="bg-[#E2E5EC]/60 px-4 py-2.5 border-b border-[#CED4DE]">
                  <h4 className="text-sm font-semibold text-[#12356B]">Team Progress</h4>
                </div>
                <div className="divide-y divide-[#CED4DE]">
                  {gameState.players.filter(p => p.isAlive).map((p) => {
                    const playerQuestions = currentQuestions.filter(qId =>
                      questionHandler.isQuestionAvailableForPlayer(qId, p.playerId, gameState)
                    );
                    const answeredCount = playerQuestions.filter(qId => p.decisions.has(qId)).length;
                    const totalCount = playerQuestions.length;
                    const allDone = totalCount > 0 && answeredCount >= totalCount;

                    return (
                      <div key={p.playerId} className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`size-8 rounded-full flex items-center justify-center text-sm font-medium ${allDone ? 'bg-[#367A25]/10 text-[#367A25]' : 'bg-[#E2E5EC] text-slate-500'}`}>
                            {p.name.charAt(0)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{p.name}</p>
                            <p className="text-xs text-slate-500">{p.role}</p>
                          </div>
                        </div>
                        {allDone ? (
                          <span className="flex items-center gap-1.5 text-sm font-medium text-[#367A25]">
                            <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            Done
                          </span>
                        ) : (
                          <span className="flex items-center gap-2 text-sm text-slate-500">
                            <svg className="size-4 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            {answeredCount}/{totalCount}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-center gap-2 mt-6">
                <div className="w-2.5 h-2.5 bg-[#367A25] rounded-full animate-pulse"></div>
                <div className="w-2.5 h-2.5 bg-[#367A25] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2.5 h-2.5 bg-[#367A25] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GameView;
